import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {ModalController, NavController} from "@ionic/angular";
import {BarcodeFormat, BarcodeScanner} from '@capacitor-mlkit/barcode-scanning';
import {StorageService} from '../storage/storage.service';
import {PlatformService} from './platform.service';
import {ToastService} from './toast.service';
import {BarcodeScanModalPage} from '../../modals/barcode-scan-modal/barcode-scan-modal.page';

@Injectable({
  providedIn: 'root'
})
export class ScannerService {

  constructor(
    private router: Router,
    private navController: NavController,
    private storageService: StorageService,
    private platformService: PlatformService,
    private modalController: ModalController,
    private toastService: ToastService
  ) {
  }

  // scans one code and stays on the current page (unlike openScanner), null if the scan was canceled
  async scanCode(title: string = 'Scannen'): Promise<string> {
    if (!this.platformService.isApp()) {
      const modal = await this.modalController.create({
        component: BarcodeScanModalPage,
        componentProps: { Title: title },
        cssClass: 'space-around height-50'
      });
      await modal.present();
      const response = await modal.onDidDismiss();
      return response?.data?.Code || null;
    }
    try {
      if (this.platformService.isAndroid()) {
        // Android: scan() uses the Google Barcode Scanner module and needs no camera permission
        const {available} = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
        if (!available) {
          await BarcodeScanner.installGoogleBarcodeScannerModule();
        }
      } else {
        const {camera} = await BarcodeScanner.requestPermissions();
        if (camera !== 'granted' && camera !== 'limited') {
          await this.toastService.presentError('Die Kamera muss aktiviert und zugelassen werden.');
          return null;
        }
      }
      const formats = [BarcodeFormat.Ean13, BarcodeFormat.Ean8, BarcodeFormat.UpcA, BarcodeFormat.UpcE, BarcodeFormat.Code128];
      const {barcodes} = await BarcodeScanner.scan({formats});
      return barcodes?.[0]?.rawValue || null;
    } catch (e) {
      // scan canceled
      return null;
    }
  }

  async setScanResult(code: any) {
    const navigationTarget = await this.getRoute(code);
    if (!navigationTarget) {
      return this.navController.back();
    }
    await this.removeScannerTarget();
    await this.clearScannerConfig();
    return this.router.navigateByUrl(navigationTarget);
  }

  async getRoute(code: any): Promise<string> {
    const navigationTarget = await this.getScannerTarget();
    const config = await this.getScannerConfig('textTransform');
    let route;
    try {
      const obj = JSON.parse(code);
      if (obj.View || obj.VIEW || obj.view) {
        route = (obj.View || obj.VIEW || obj.view).toLowerCase();
      }
    } catch (e) {
    }
    try {
      if (config) {
        code = code[config]();
      }
    } catch (e) {

    }
    return route || (navigationTarget + '/' + code);
  }

  async openScanner(navigationTarget: string = '', config: any = {}) {
    await this.saveScannerConfig(config);
    await this.saveScannerTarget(navigationTarget);
    await this.saveScannerBack(this.router.url);
    return this.router.navigate(['/scanner'], { skipLocationChange: true, replaceUrl: true });
  }

  async getTitle(): Promise<string> {
    return (await this.getScannerConfig('title')) || 'Scannen';
  }

  async showInput(): Promise<boolean> {
    return await this.getScannerConfig('showInput');
  }

  async clearScannerConfig() {
    return this.storageService.remove('scanner-config');
  }

  async saveScannerConfig(newConfig: any) {
    const config = await this.getScannerConfigObj();
    for (const key in newConfig) {
      config[key] = newConfig[key];
    }
    return this.storageService.save('scanner-config', JSON.stringify(config));
  }

  async getScannerConfig(key: string) {
    return (await this.getScannerConfigObj())[key];
  }

  private async getScannerConfigObj() {
    const config = await this.storageService.get('scanner-config');
    return config ? JSON.parse(config) : {};
  }

  async removeScannerTarget() {
    return this.storageService.remove('scanner-target');
  }

  async saveScannerTarget(url: string) {
    return this.storageService.save('scanner-target', url);
  }

  async getScannerTarget(): Promise<string> {
    return this.storageService.get('scanner-target');
  }

  async saveScannerBack(url: string) {
    return this.storageService.save('scanner-back', url);
  }

  async getScannerBack(): Promise<string> {
    return this.storageService.get('scanner-back');
  }
}
